// src/components/Admin/ChatManager/Category/CategoryList.jsx
import React, { useState } from 'react';
import {
    TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    IconButton, Snackbar, Alert, Chip, Accordion, AccordionSummary,
    AccordionDetails, Typography, Box, useMediaQuery, Tooltip
} from '@mui/material';
import {
    ArrowUpward, ArrowDownward, Edit, Delete,
    Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
    moveChatCategory,
    deleteChatCategory,
    editChatCategory,
} from '../../../../api/admin';

/**
 * @param {Array}  categories          - 카테고리 배열
 * @param {Func}   onEdit              - 수정 모달 열기
 * @param {Func}   reload              - 관리자 테이블만 새로고침
 * @param {Func}   reloadCategories    - ★ 사이드바 동기화용 (Context의 loadCategories)
 */
const ChatCategoryList = ({
    categories = [],
    onEdit,
    reload,
    reloadCategories,          // ★ 추가
}) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /* ───── 유틸 ─────────────────────────────────────────── */
    const notify = (msg) => {
        setSnackbarMsg(msg);
        setSnackbarOpen(true);
    };

    /* ───── 순서 이동 ───────────────────────────────────── */
    const handleMove = (id, dir) => {
        moveChatCategory(id, dir)
            .then(() => {
                reload();                // 관리자 표 새로고침
                reloadCategories?.();    // ★ 사이드바 실시간 동기화
                notify('순서가 바뀌었습니다!');
            })
            .catch(() => notify('순서 변경에 실패했습니다.'));
    };

    /* ───── 삭제 ───────────────────────────────────────── */
    const handleDelete = (id) => {
        if (!window.confirm('정말 삭제할까요?')) return;
        deleteChatCategory(id)
            .then(() => {
                reload();
                reloadCategories?.();    // ★
                notify('채팅 카테고리가 삭제되었습니다.');
            })
            .catch(() => notify('삭제에 실패했습니다.'));
    };

    /* ───── 노출 / 숨김 토글 ───────────────────────────── */
    const handleToggleActive = (cat) => {
        editChatCategory(cat.id, { is_active: !cat.is_active })
            .then(() => {
                reload();
                reloadCategories?.();    // ★
                notify(cat.is_active ? '숨김 처리되었습니다.' : '노출 처리되었습니다.');
            })
            .catch(() => notify('상태 변경에 실패했습니다.'));
    };

    /* ───── 렌더링 (모바일 vs 데스크톱) ─────────────────── */
    return (
        <>
            {/* ===== 모바일: 아코디언 뷰 ===== */}
            {isMobile ? (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {categories.map((cat) => (
                        <Accordion key={cat.id}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        pr: 2,
                                    }}
                                >
                                    <Typography fontWeight="bold">
                                        {cat.emoji} {cat.name}
                                    </Typography>

                                    <Chip
                                        label={cat.is_active ? '노출중' : '숨김'}
                                        size="small"
                                        color={cat.is_active ? 'success' : 'default'}
                                        icon={
                                            cat.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleActive(cat);
                                        }}
                                        clickable
                                        sx={{ alignSelf: 'flex-start' }}
                                    />
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {cat.description}
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        justifyContent: 'center',
                                        mb: 1,
                                    }}
                                >
                                    <Chip
                                        label={`익명: ${cat.anonymous_option !== 'none' ? 'O' : 'X'
                                            }`}
                                        size="small"
                                    />
                                    <Chip
                                        label={`비공개: ${cat.is_private_only ? 'O' : 'X'}`}
                                        size="small"
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <Tooltip title="위로 이동">
                                        <IconButton
                                            size="small"
                                            sx={{ width: 36, height: 36 }}
                                            onClick={() => handleMove(cat.id, 'up')}
                                        >
                                            <ArrowUpward fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="아래로 이동">
                                        <IconButton
                                            size="small"
                                            sx={{ width: 36, height: 36 }}
                                            onClick={() => handleMove(cat.id, 'down')}
                                        >
                                            <ArrowDownward fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Box
                                    sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}
                                >
                                    <Tooltip title="수정">
                                        <IconButton
                                            size="small"
                                            sx={{ width: 36, height: 36 }}
                                            onClick={() => onEdit(cat)}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="삭제">
                                        <IconButton
                                            size="small"
                                            sx={{ width: 36, height: 36 }}
                                            onClick={() => handleDelete(cat.id)}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            ) : (
                /* ===== 데스크톱: 테이블 뷰 ===== */
                <TableContainer
                    component={Paper}
                    sx={{
                        mt: 2,
                        '& .MuiTableCell-root': { py: 1, px: 2 },
                        '& .MuiTableHead-root': { bgcolor: 'grey.100' },
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" width={80}>
                                    순서
                                </TableCell>
                                <TableCell>이모지</TableCell>
                                <TableCell>카테고리명</TableCell>
                                <TableCell>설명</TableCell>
                                <TableCell align="center">익명</TableCell>
                                <TableCell align="center">공개/비공개</TableCell>
                                <TableCell align="center">상태</TableCell>
                                <TableCell align="center" width={120}>
                                    관리
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {categories.map((cat) => (
                                <TableRow key={cat.id} hover>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMove(cat.id, 'up')}
                                        >
                                            <ArrowUpward fontSize="inherit" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMove(cat.id, 'down')}
                                        >
                                            <ArrowDownward fontSize="inherit" />
                                        </IconButton>
                                    </TableCell>

                                    <TableCell>{cat.emoji}</TableCell>
                                    <TableCell>{cat.name}</TableCell>
                                    <TableCell>{cat.description}</TableCell>

                                    <TableCell align="center">
                                        {cat.anonymous_option !== 'none' ? '익명' : '공개'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {cat.is_private_only ? '비공개' : '공개'}
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip
                                            label={cat.is_active ? '노출중' : '숨김'}
                                            size="small"
                                            color={cat.is_active ? 'success' : 'default'}
                                            icon={
                                                cat.is_active ? (
                                                    <VisibilityIcon />
                                                ) : (
                                                    <VisibilityOffIcon />
                                                )
                                            }
                                            onClick={() => handleToggleActive(cat)}
                                            clickable
                                        />
                                    </TableCell>

                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => onEdit(cat)}>
                                            <Edit fontSize="inherit" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(cat.id)}
                                        >
                                            <Delete fontSize="inherit" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ───── 스낵바 ───────────────────────────────────── */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    {snackbarMsg}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ChatCategoryList;